// src/components/authentication/ForgotPasswordRequest.js
/* ------------------------------------------------------------------ */
/* ForgotPasswordRequest – send reset-link e-mail                      */
/* ------------------------------------------------------------------ */
/**
 * • Collects user’s e-mail → `sendPasswordResetEmail()`.
 * • Shows confirmation that a link was sent (even if the e-mail is unknown,
 *   to avoid account enumeration).
 */

import React, { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase/firebaseApp";
import { EMAIL_REGEX } from "../../utils/constants";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import { useTranslation } from "react-i18next";

/* Firebase singleton */
const auth = getAuth(firebaseApp);

/* Component */
export default function ForgotPasswordRequest() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    closePopup();

    if (!EMAIL_REGEX.test(email)) {
      openPopup({ message: t("forgotPassword.emailRegexError") });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email, {
        // optional: customise Firebase-hosted page URL
        url: `${window.location.origin}/auth/reset-password`,
        handleCodeInApp: true,
      });
      setSent(true);
    } catch (err) {
      openPopup({ message: t("forgotPassword.emailSendError") });
    }
  };

  return (
    <div className="w-full max-w-lg h-full flex flex-col justify-end gap-4 items-stretch p-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}
      {/* logo + heading */}
      <div className="w-full h-full flex flex-col items-center justify-start pt-24 z-10">
        <img
          src="/purpledx_primary_logo.png"
          className="h-auto w-full title-text"
          alt="purpleDx"
        />{" "}
        <h2 className="subtitle-text text-center">{t("landing.subtitle")}</h2>
      </div>
      <h1 className="title-text">{t("forgotPassword.title")}</h1>

      {sent ? (
        <>
          <p className="text-center">
            {t("forgotPassword.exists1")}
            <strong>{email}</strong> {t("forgotPassword.exists2")}
          </p>
          <Link to="/auth/login" className="link">
            {t("forgotPassword.back")}
          </Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <input
            type="email"
            placeholder={t("forgotPassword.email")}
            className="custom-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="primary-button">
            {t("forgotPassword.cta")}
          </button>
        </form>
      )}
    </div>
  );
}
